import { useTranslation } from 'react-i18next'
import { MemberAvatar } from '@/components/clubs'
import { cn } from '@/lib/utils'
import type { ChallengeCategory, ChallengeEntry, ClubMember } from '@/types'

interface CategoryCardProps {
  category: ChallengeCategory
  index: number
  entries: ChallengeEntry[]
  members: ClubMember[]
  myUserId: string | undefined
  commentTotal: number
  onOpen: () => void
}

/** Grid cell: category, who suggested it, and each member's status */
export function CategoryCard({
  category,
  index,
  entries,
  members,
  myUserId,
  commentTotal,
  onOpen,
}: CategoryCardProps) {
  const { t } = useTranslation()
  const mine = entries.find((e) => e.user_id === myUserId)
  const suggester = members.find((m) => m.user_id === category.suggested_by)

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex h-full min-w-0 flex-col gap-3 rounded-lg border bg-card p-4 text-left shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        mine?.completed_on ? 'border-success/40' : mine ? 'border-warning/40' : 'border-border'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="shrink-0 text-xs font-semibold text-muted-foreground">#{index + 1}</span>
        <p className="min-w-0 flex-1 break-words text-sm font-medium text-foreground">
          {category.title}
        </p>
        <span className="shrink-0 text-base" aria-hidden="true">
          {mine?.completed_on ? '✅' : mine ? '📖' : '⬜'}
        </span>
      </div>

      {mine && (
        <p className="truncate text-xs text-muted-foreground">
          {t('clubs.members.you')}: {mine.book_title}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between gap-2">
        <div className="flex -space-x-1.5">
          {members.map((member) => {
            const entry = entries.find((e) => e.user_id === member.user_id)
            return (
              <span
                key={member.user_id}
                className={cn(
                  'rounded-full ring-2',
                  entry?.completed_on
                    ? 'ring-success'
                    : entry
                      ? 'ring-warning'
                      : 'ring-muted opacity-40'
                )}
                title={`${member.display_name}: ${
                  entry?.completed_on
                    ? t('challenges.entry.done')
                    : entry
                      ? t('challenges.entry.picked')
                      : t('challenges.entry.none')
                }`}
              >
                <MemberAvatar
                  userId={member.user_id}
                  name={member.display_name}
                  avatarUrl={member.avatar_url}
                  size="xs"
                />
              </span>
            )
          })}
        </div>
        <span className="truncate text-[11px] text-muted-foreground">
          {commentTotal > 0 && `💬 ${commentTotal} · `}
          {suggester ? t('challenges.categories.by', { name: suggester.display_name }) : ''}
        </span>
      </div>
    </button>
  )
}
